import { Component, ElementRef, EventEmitter, Input, NgZone, Output, ViewChild, ViewContainerRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { JanusWindowSizeService, ValkyrieBackendService } from "@symplicity/syng";
import { fromEvent, Subscription } from "rxjs";
import { filter } from "rxjs/operators";

declare var window: Window;

type TabNavigationElement = 'pause' | 'prev' | 'next' | 'cta';

@Component({
    selector: 'hero-banner',
    templateUrl: 'hero-banner.component.html',
    standalone: false
})
export class HeroBannerComponent {
    @ViewChild("bannerFlickity", {static: false}) bannerFlickityEl: ElementRef<HTMLDivElement>;
    @ViewChild("liveRegion", {static: false}) liveRegionEl: ElementRef<HTMLSpanElement>;
    @ViewChild("customChevron", { static: false }) customChevron: ElementRef<HTMLSpanElement>;

    bannerFlickity: any;
    bannerSubscription: Subscription;
    bannerFocusSubscription: Subscription;
    bannerBlurSubscription: Subscription;
    bannerData: any;
    screenSize: string = '';
    selectedSlideIndex = 0;

    @Input() translationDomain = 'home hero_banner';
    @Input() customButtons: Record<string, any>;
    @Input() pauseButton = false;
    @Input() disableFirstPrevLastNext = false;
    @Input() tabNavigationOrder: TabNavigationElement[] = ['pause', 'prev', 'next', 'cta'];
    @Input() xsScreensTabNavigationOrder: TabNavigationElement[];

    @Output() onOpenModal = new EventEmitter<any>();
    @Output() emptyModels = new EventEmitter<void>();

   bannerFlickityOptions = {
        "wrapAround": true,
        "autoPlay": 5000,
        "rightToLeft": window.document.dir === 'rtl',
        "accessibility": false,
        "imagesLoaded": true,
        "prevNextButtons": true,
        "pageDots": true
    }
    bannerFlickityPaused = false;

    KEYS = {
        ENTER: 'Enter',
        SPACE: ' ',
        LEFT:  'ArrowLeft',
        UP:    'ArrowUp',
        RIGHT: 'ArrowRight',
        DOWN:  'ArrowDown',
        TAB:   'Tab'
    };

    get isScreenSizeXS(): boolean {
        /**
         * FIXME: This only works for projects using Janus, as `JanusWindowSizeService` is based on
         * `--bs-breakpoint-xx` variables from @symplicity/bootstrap, while some projects only have
         * `--breakpoint-xx` variables
         */
        return this.screenSize === 'xs';
    }

    get flickityPrevButton(): HTMLElement {
        return this.bannerFlickityEl.nativeElement.querySelector('.flickity-prev-next-button.previous');
    }

    get flickityNextButton(): HTMLElement {
        return this.bannerFlickityEl.nativeElement.querySelector('.flickity-prev-next-button.next');
    }

    constructor (
        private backendService: ValkyrieBackendService,
        private translate: TranslateService,
        private zone: NgZone,
        private windowSize: JanusWindowSizeService
    ) {
        this.intersectionCallback = this.intersectionCallback.bind(this);
        this.windowSize.breakpoints$().pipe(takeUntilDestroyed()).subscribe((size) => {
            this.screenSize = size;
            if (this.bannerFlickity) {
                this.setupTabNavigation();
            }
        })
    }

    ngOnInit() {
        this.backendService.getAll('announcements', 'v2',{ listdef: 'HeroBannerAnnouncementList', perPage:'5' })
            .subscribe( res => {
                if (!res.models.length) {
                    this.bannerData = res;
                    this.emptyModels.emit();
                    return;
                }
                if (res.models.length === 1) {
                    this.bannerFlickityOptions.pageDots = false;
                    this.bannerFlickityOptions.prevNextButtons = false;
                    this.pauseButton = false;
                }

                res.models = res.models.map(model => {
                    model.use_background_overlay = false;

                    if (model.background_image) {
                        const background_position = model.background_image_alignment ? model.background_image_alignment.replace("_", " ") : "center center";
                        model.extra_styles = {"background-image": "url(" + model.background_image + ")", "background-position": background_position};
                        model.use_background_overlay = true;
                    }
                    if (model.custom_link_override) {
                        model.target = "_self";
                        const url = new URL(model.custom_link_override);
                        if (url.protocol === "http:") {
                            model.custom_link_override = model.custom_link_override.replace("http:", "https:")
                        }
                        if (url.host !== window.location.host) {
                            model.target = "_blank"
                        }
                    }
                    return model;
                })
                this.bannerData = res;
            });
        }

    ngOnDestroy() {
        if(this.bannerSubscription) { this.bannerSubscription.unsubscribe(); }
        if(this.bannerBlurSubscription) { this.bannerBlurSubscription.unsubscribe(); }
        if(this.bannerFocusSubscription) { this.bannerFocusSubscription.unsubscribe(); }
        this.onOpenModal.unsubscribe();
    }

    onStaticClick() {
        this.openModal(this.bannerFlickity.selectedIndex)
    }

    bannerReady(flickity) {
        this.bannerFlickity = flickity;
        this.bannerSubscription = this.flickityKeyboardListener(this.bannerFlickityEl.nativeElement, this.bannerFlickity);
        this.bannerBlurSubscription = fromEvent(this.bannerFlickityEl.nativeElement, 'focusout').subscribe(() => {
            !this.bannerFlickityPaused && this.bannerFlickity.playPlayer();
        })
        this.bannerFocusSubscription = fromEvent(this.bannerFlickityEl.nativeElement, 'focusin').subscribe(() => {
            this.bannerFlickity.stopPlayer();
        })

        if (this.bannerFlickity.slides.length > 1) {
            if (this.disableFirstPrevLastNext) {
                this.setDisabledNextPrev();
            }
            this.setupPrevNextCustomChevron();
            this.setupTabNavigation();
        }
    }

    intersectionCallback(entries: IntersectionObserverEntry[]) {
        if (!this.bannerFlickity) { return }
        if (!entries[0].isIntersecting) {
            this.bannerFlickity.stopPlayer();
        } else {
            if (!document.activeElement.closest('.hero-announcement-carousel-wrapper')) {
                this.bannerFlickity.playPlayer();
            }
        }
    }

    openModal(cellIndex: number) {
        if (this.bannerData.models[cellIndex].custom_link_override) return; // If item has custom_link_override, don't show modal.
        this.onOpenModal.emit(this.bannerData.models[cellIndex].announcement_id);
        this.bannerFlickity.stopPlayer();
    }

    onPause() {
        if (this.bannerFlickityPaused) {
            this.bannerFlickity.playPlayer();
        } else {
            this.bannerFlickity.stopPlayer();
        }

        this.bannerFlickityPaused = !this.bannerFlickityPaused
    }

    onChange(selectedIndex: number): void {
        this.selectedSlideIndex = selectedIndex;
        if (this.bannerFlickityEl.nativeElement.contains(document.activeElement)) {
            this.updateAriaLiveRegion(this.selectedSlideIndex, this.bannerFlickity.slides.length);
        }
    }

    updateAriaLiveRegion(selectedIndex: number, totalSlides: number) {
        this.liveRegionEl.nativeElement.textContent = this.translate.instant(
            `${this.translationDomain}.Announcement x of y`,
            { position: selectedIndex + 1, total: totalSlides }
        );
    }

    flickityKeyboardListener(flickityElement, flickity) {
        return fromEvent<KeyboardEvent>(flickityElement, 'keyup').pipe(
            filter((e) => (Object.values(this.KEYS).includes(e.key) && e.altKey))
            ).subscribe(e => {
                switch (e.key) {
                    case this.KEYS.LEFT:
                    case this.KEYS.UP: {
                        this.handleAxFlow(flickity, false);
                        break;
                    }
                    case this.KEYS.RIGHT:
                    case this.KEYS.DOWN: {
                        this.handleAxFlow(flickity, true);
                        break;
                    }
                }
            }
            )
    }

    handleAxFlow(flickity: any, next: boolean) {
        if (next) {
            flickity.next(true, true); // 'next' and 'previous' params:  isWrapped: wrap around the elements | isInstant: without animation.
        } else {
            flickity.previous(true, true);
        }

        this.bannerFlickity.selectedElement.querySelector(".hero-announcement-call-to-action").focus();
    }

    setDisabledNextPrev() {
        const prevButton = this.bannerFlickityEl.nativeElement.querySelector('.flickity-prev-next-button.previous');
        prevButton.setAttribute('disabled', 'true');
        this.bannerFlickity.on('change', (slide) => {
            const nextButton = this.bannerFlickityEl.nativeElement.querySelector('.flickity-prev-next-button.next');
            const prevButton = this.bannerFlickityEl.nativeElement.querySelector('.flickity-prev-next-button.previous');

            if (slide === (this.bannerFlickity.slides.length -1)) {
                nextButton.setAttribute('disabled', 'true');
            } else {
                nextButton.removeAttribute('disabled');
            }
            if (slide === 0) {
                prevButton.setAttribute('disabled', 'true');
            } else {
                prevButton.removeAttribute('disabled');
            }
        })
    }

    private setupPrevNextCustomChevron(): void {
        if ((this.flickityPrevButton || this.flickityNextButton) && this.customButtons?.chevron) {
            const customChevron = this.customChevron.nativeElement.innerHTML;
            this.flickityPrevButton.innerHTML = customChevron;
            this.flickityNextButton.innerHTML = customChevron;
            this.customChevron.nativeElement.remove();
        }

        this.flickityPrevButton?.setAttribute('aria-label', this.translate.instant('misc.Previous'));
        this.flickityNextButton?.setAttribute('aria-label', this.translate.instant('misc.Next'));
    }

    private setupTabNavigation(): void {
        const tabNavigationHTMLElements: Record<TabNavigationElement, HTMLElement> = {
            pause: this.bannerFlickityEl.nativeElement.querySelector('.flickity-pause-button'),
            prev: this.flickityPrevButton,
            next: this.flickityNextButton,
            cta: this.bannerFlickityEl.nativeElement.querySelector('.flickity-viewport'),
        };
        const tabNavigationOrder = (this.xsScreensTabNavigationOrder && this.isScreenSizeXS)
            ? this.xsScreensTabNavigationOrder
            : this.tabNavigationOrder;
        const parentElement = this.bannerFlickityEl.nativeElement.querySelector('.hero-announcement-carousel');

        tabNavigationOrder.forEach((el) => parentElement.appendChild(tabNavigationHTMLElements[el]))
    }
}
